import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { ICameraEvent } from 'app/shared/model/camera-event.model';
import { ICameraEventMedia } from 'app/shared/model/camera-event-media.model';
import { IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

interface ICameraEventParams extends IQueryParams {
  excludeSystemEvents?: boolean;
  searchCompanyName?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  eventType?: string | null;
  licensePlateSearch?: string | null;
  truckImplementId?: number | null;
}

interface CameraEventState {
  loading: boolean;
  errorMessage: string | null;
  entities: ReadonlyArray<ICameraEvent>;
  totalItems: number;
  media: ReadonlyArray<ICameraEventMedia>;
  mediaLoading: boolean;
  mediaRequestId: string | null;
}

const initialState: CameraEventState = {
  loading: false,
  errorMessage: null,
  entities: [],
  totalItems: 0,
  media: [],
  mediaLoading: false,
  mediaRequestId: null,
};

const apiUrl = 'api/camera-events';

export const getEntities = createAsyncThunk(
  'cameraEvent/fetch_entity_list',
  async ({ searchCompanyName, startAt, endAt, eventType, licensePlateSearch, truckImplementId, page, size, sort, excludeSystemEvents }: ICameraEventParams) => {
    const searchParams = new URLSearchParams({
      page: String(page ?? 0),
      size: String(size ?? 20),
      cacheBuster: String(new Date().getTime()),
    });

    if (searchCompanyName) searchParams.set('searchCompanyName', searchCompanyName);
    if (startAt) searchParams.set('startAt', startAt);
    if (endAt) searchParams.set('endAt', endAt);
    if (eventType) searchParams.set('eventType', eventType);
    if (licensePlateSearch) searchParams.set('licensePlateSearch', licensePlateSearch);
    if (truckImplementId) searchParams.set('truckImplementId', String(truckImplementId));
    if (sort) searchParams.set('sort', sort);
    if (excludeSystemEvents) searchParams.set('excludeSystemEvents', 'true');

    return axios.get<ICameraEvent[]>(`${apiUrl}?${searchParams.toString()}`);
  },
  { serializeError: serializeAxiosError }
);

export const getMedia = createAsyncThunk(
  'cameraEvent/fetch_media',
  async ({ hatDeviceCode, hatEventId }: { hatDeviceCode: string; hatEventId: string }) =>
    axios.get<ICameraEventMedia[]>(`${apiUrl}/${encodeURIComponent(hatDeviceCode)}/${encodeURIComponent(hatEventId)}/media`),
  { serializeError: serializeAxiosError }
);

export const CameraEventSlice = createSlice({
  name: 'cameraEvent',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(getMedia.pending, (state, action) => {
        state.errorMessage = null;
        state.media = [];
        state.mediaLoading = true;
        state.mediaRequestId = action.meta.requestId;
      })
      .addCase(getMedia.fulfilled, (state, action) => {
        if (state.mediaRequestId !== action.meta.requestId) return;
        state.mediaLoading = false;
        state.media = action.payload.data;
        state.mediaRequestId = null;
      })
      .addCase(getMedia.rejected, (state, action) => {
        if (state.mediaRequestId !== action.meta.requestId) return;
        state.mediaLoading = false;
        state.media = [];
        state.mediaRequestId = null;
        state.errorMessage = action.error.message || null;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'] || '0', 10);
      })
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isRejected(getEntities), (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || null;
      });
  },
});

export const { reset } = CameraEventSlice.actions;

export default CameraEventSlice.reducer;
